import { useCallback, useLayoutEffect, useState } from 'react';
import { useDashboardContext } from '@features/dashboard/Dashboard';

export type TileConfigSync = {
    saveForEveryone: () => void;
    resetConfig: () => void;
};

// Coordinate save-for-everyone between tiles
export const useTileConfigState = () => {
    const [pending, setPending] = useState<TileConfigSync[]>([]);
    const canSaveForEveryone = !!pending.length;

    const subscribeConfig = useCallback((config: TileConfigSync) => {
        setPending((pending) => [...pending, config]);
        return () => {
            setPending((pending) => pending.filter((c) => c !== config));
        };
    }, []);

    const saveForEveryone = useCallback(() => pending.forEach((p) => p.saveForEveryone()), [pending]);
    const resetConfig = useCallback(() => pending.forEach((p) => p.resetConfig()), [pending]);

    return { subscribeConfig, canSaveForEveryone, saveForEveryone, resetConfig };
};

// Connect single config to global saver if changed
export const useTileConfigSync = (
    canSaveForEveryone: boolean,
    saveForEveryone: () => void,
    resetConfig: () => void
) => {
    const { subscribeConfig } = useDashboardContext();
    useLayoutEffect(() => {
        if (!canSaveForEveryone) return;
        return subscribeConfig({ saveForEveryone, resetConfig });
    }, [canSaveForEveryone, saveForEveryone, subscribeConfig, resetConfig]);
};
